import { IBotData, IPerformanceDataPoint, IPosition } from "../shared/types";
import { processVolumeData } from "./processVolumeData";
import { capitalBasis, isTreasuryBuyback } from "../utils/botMetrics";

const DAY = 86_400_000;

/** Keeps at most `max` evenly spaced points (always including the last one). */
export const downsample = (values: number[], max = 48): number[] => {
  if (values.length <= max) return values;
  const step = (values.length - 1) / (max - 1);
  return Array.from({ length: max }, (_, i) => values[Math.round(i * step)]);
};

const movingAverage = (values: number[], window = 3): number[] =>
  values.map((_, i) => {
    const from = Math.max(0, i - window + 1);
    const slice = values.slice(from, i + 1);
    return slice.reduce((a, b) => a + b, 0) / slice.length;
  });

const feesUSD = (p: IPosition): number => {
  const snap = p.balanceSnapshotEnd || p.balanceSnapshotStart;
  if (!snap) return 0;
  return (p.feesObtainedToken0 || 0) * (snap.token0Price || 0) + (p.feesObtainedToken1 || 0) * (snap.token1Price || 0);
};

/**
 * Weekly volume provided (smoothed) — activity trend for the volume tile. The current week is still
 * filling up, so it's left out (it would always end the line with a dip).
 */
export const weeklyVolumeTrend = (positions: IPosition[]): number[] => {
  const weeks = processVolumeData(positions, "weekly");
  const lastStart = weeks.length > 0 ? Date.parse(weeks[weeks.length - 1].date) : NaN;
  const complete = Number.isFinite(lastStart) && Date.now() - lastStart < 7 * DAY ? weeks.slice(0, -1) : weeks;
  return downsample(movingAverage(complete.map((p) => p.volume)));
};

/**
 * Cumulative pool fees over time. Each position's fees are spread evenly over its
 * lifetime (same assumption the volume series uses).
 */
export const cumulativeFeesTrend = (positions: IPosition[]): number[] => {
  if (!positions || positions.length === 0) return [];
  const daily = new Map<number, number>();
  let first = Infinity;
  let last = -Infinity;
  for (const p of positions) {
    const fees = feesUSD(p);
    if (!fees || !Number.isFinite(fees)) continue;
    const start = Math.floor(p.date / DAY);
    const end = Math.floor((p.isOpen ? Date.now() : p.endDate || p.date) / DAY);
    const days = Math.max(1, end - start + 1);
    for (let d = start; d <= end; d++) daily.set(d, (daily.get(d) || 0) + fees / days);
    first = Math.min(first, start);
    last = Math.max(last, end);
  }
  if (!Number.isFinite(first)) return [];
  const out: number[] = [];
  let acc = 0;
  for (let d = first; d <= last; d++) {
    acc += daily.get(d) || 0;
    out.push(acc);
  }
  return downsample(out);
};

/**
 * Bonus yield over time, in USD like the tile it sits under: treasury buybacks valued at their
 * token's current USD price (backend `curPrice`) plus manual premiums, by date. Mixing token counts
 * (FET + WETH + GNO) would just trace whichever token has the most units.
 */
export const cumulativeBuybackTrend = (bots: IBotData[]): number[] => {
  const events: { at: number; usd: number }[] = [];
  bots.forEach((bot) => {
    (bot.buybacks || []).forEach((b) => {
      if (!isTreasuryBuyback(bot.stats, b) || !Number.isFinite(b.lastUpdatedAt)) return;
      const usd = (b.totalBaseBought || 0) * (b.curPrice || 0);
      if (usd > 0) events.push({ at: b.lastUpdatedAt, usd });
    });
    (bot.movements || []).forEach((m) => {
      if (m.movementType !== "MANUAL_PREMIUM" || !(m.amountUSD > 0)) return;
      const at = new Date(m.date).getTime();
      if (Number.isFinite(at)) events.push({ at, usd: m.amountUSD });
    });
  });
  if (events.length === 0) return [];
  events.sort((a, b) => a.at - b.at);
  let acc = 0;
  const out = events.map((e) => (acc += e.usd));
  return downsample(out.length === 1 ? [0, out[0]] : [0, ...out]);
};

/** LP index of a set of bots, for card sparklines. */
export const poolIndexTrend = (points: { poolIndex: number }[]): number[] =>
  downsample(points.map((p) => p.poolIndex).filter((v) => Number.isFinite(v)), 60);

/**
 * One day of one bot; `hodl` is the backend's HODL value that day (deposited tokens × USD prices)
 * and `basis` that day's capital basis, when sent.
 */
type Snap = { cap: number; price: number; pnl: number; hodl?: number; basis?: number };

const utcDay = (ts: number) => {
  const d = new Date(ts);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

/**
 * LP vs HODL index for any set of bots (one bot, a family, or the whole portfolio), built from
 * the backend's own figures so the chart, the hero chip and every card agree:
 *   LP(t)   = 1,000 × (1 + Σ pnl_i(t) / B)
 *   HODL(t) = 1,000 × (1 + Σ hodlPnl_i(t) / B)
 * hodlPnl is the backend's HODL value − basis when it sends one (holding the deposited tokens, in
 * USD; a day's basis is exactly capital − P&L); otherwise basis · (price / initialPrice − 1), the
 * pair price (older payloads).
 * B is today's capital basis (Σ initial + deposits − withdrawals). A fixed denominator keeps the
 * curves free of dilution dips when a bot launches or capital is added (a per-day basis would
 * make the line drop every time new money arrives). The live point uses pnl − hodlDiffUSD as the
 * HODL P&L, so LP − HODL = Σ hodlDiffUSD / B exactly — the cards' "vs HODL", whichever method the
 * backend uses. Bots carry their last snapshot forward, so a missing day never creates a jump.
 */
export function processPortfolioHistory(bots: IBotData[]): IPerformanceDataPoint[] {
  const perBot = bots
    .map((bot) => {
      const days = new Map<number, Snap>();
      let lastHodl: number | undefined;
      [...(bot.history || [])]
        .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
        .forEach((h) => {
          // A snapshot without P&L would read as a drop to the basis: skip it (the previous one carries forward).
          if (!(h.totalCapitalUSD > 0) || !(h.price > 0) || !Number.isFinite(h.pnlUSD)) return;
          const hodl = Number.isFinite(h.hodlValueUSD) && (h.hodlValueUSD as number) > 0 ? h.hodlValueUSD : undefined;
          // A day without prices keeps the previous HODL value rather than switching method mid-line.
          if (hodl !== undefined) lastHodl = hodl;
          days.set(utcDay(h.timestamp), {
            cap: h.totalCapitalUSD,
            price: h.price,
            pnl: h.pnlUSD as number,
            hodl: hodl ?? lastHodl,
            basis: Number.isFinite(h.capitalBasisUSD) && (h.capitalBasisUSD as number) > 0 ? h.capitalBasisUSD : undefined,
          });
        });
      const firstPrice = Array.from(days.values())[0]?.price;
      const initialPrice = bot.stats?.initialPrice > 0 ? bot.stats.initialPrice : firstPrice;
      // One method per bot for its whole line: HODL values whenever the backend sends them (days before
      // its first value — before the first position — hold nothing yet: HODL P&L 0).
      const useHodlValue = Array.from(days.values()).some((d) => d.hodl !== undefined);
      return { bot, days, initialPrice, useHodlValue };
    })
    // Bots with a capital basis ≤ 0 have no meaningful return (see hasReturnBasis): left out.
    .filter((b) => b.days.size > 0 && b.initialPrice > 0 && (!b.bot.stats || capitalBasis(b.bot.stats) > 0));
  if (perBot.length === 0) return [];

  // Same pair across all bots (a family) → the tooltip can show the pair price.
  const pairs = new Set(perBot.map((b) => b.bot.stats.botName.replace(/^[A-Z]+_/, "").replace(/_\d+$/, "")));
  const singlePair = pairs.size === 1;

  const allDays = Array.from(new Set(perBot.flatMap((b) => Array.from(b.days.keys())))).sort((a, b) => a - b);
  const last = new Map<IBotData, Snap>();
  const out: IPerformanceDataPoint[] = [];
  let prevPnl: number | null = null;
  let prevPrice: number | null = null;

  // Bots with a positive basis but no usable history yet still count today (basis and live point),
  // so the last point always equals the hero chip and the cards.
  const liveOnly = bots.filter(
    (b) => b.stats && capitalBasis(b.stats) > 0 && b.stats.currentPrice > 0 && !perBot.some((p) => p.bot === b)
  );

  // Today's basis (backend definition) as the common denominator; per-point basis only as a fallback.
  const referenceBasis =
    perBot.reduce((acc, b) => acc + (b.bot.stats ? capitalBasis(b.bot.stats) : 0), 0) +
    liveOnly.reduce((acc, b) => acc + capitalBasis(b.stats), 0);

  type PointInput = { snap: Snap; basis: number; initialPrice: number; useHodlValue: boolean; hodlPnl?: number };
  const point = (date: number, snaps: PointInput[]) => {
    let pointBasis = 0;
    let pnl = 0;
    let hodlPnl = 0;
    let priceSum = 0;
    snaps.forEach(({ snap, basis: b, initialPrice, useHodlValue, hodlPnl: given }) => {
      pointBasis += b;
      pnl += snap.pnl;
      hodlPnl +=
        given !== undefined
          ? given
          : useHodlValue
          ? snap.hodl !== undefined
            ? snap.hodl - b
            : 0
          : b * (snap.price / initialPrice - 1);
      priceSum += snap.price;
    });
    const basis = referenceBasis > 0 ? referenceBasis : pointBasis;
    if (!(basis > 0) || snaps.length === 0) return;
    const price = singlePair ? priceSum / snaps.length : null;
    out.push({
      date,
      // Full precision: rounding here and again when formatting would drift from the cards by 0.01 pp.
      poolIndex: 1000 * (1 + pnl / basis),
      tokenIndex: 1000 * (1 + hodlPnl / basis),
      priceChange: price != null && prevPrice ? price / prevPrice - 1 : null,
      pnlChange: prevPnl != null ? (pnl - prevPnl) / basis : null,
      previousPrice: price != null ? prevPrice : null,
      currentPrice: price,
      previousPnl: prevPnl,
      currentPnl: pnl,
    });
    prevPnl = pnl;
    prevPrice = price;
  };

  allDays.forEach((day) => {
    perBot.forEach((b) => {
      const snap = b.days.get(day);
      if (snap) last.set(b.bot, snap);
    });
    const snaps = perBot
      .filter((b) => last.has(b.bot))
      .map((b) => {
        const snap = last.get(b.bot)!;
        // That day's basis: sent by the backend, else capital − P&L (equal by construction).
        return { snap, basis: snap.basis ?? snap.cap - snap.pnl, initialPrice: b.initialPrice, useHodlValue: b.useHodlValue };
      });
    point(day, snaps);
  });

  // Live point (start of tomorrow, UTC) from the current stats, with the exact backend basis.
  const now = new Date();
  const tomorrow = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1);
  const liveSnaps: PointInput[] = perBot
    .filter((b) => b.bot.stats && b.bot.stats.currentPrice > 0)
    .map((b) => {
      const s = b.bot.stats;
      const pnl = s.pnlUSD ?? 0;
      return {
        snap: { cap: s.totalCapitalUSD, price: s.currentPrice, pnl },
        basis: capitalBasis(s),
        initialPrice: b.initialPrice,
        useHodlValue: b.useHodlValue,
        // The backend's own LP − HODL difference pins the last point to the cards' figure.
        hodlPnl: Number.isFinite(s.hodlDiffUSD) ? pnl - (s.hodlDiffUSD as number) : undefined,
      };
    });
  liveOnly.forEach((b) => {
    const s = b.stats;
    const pnl = s.pnlUSD ?? 0;
    const basis = capitalBasis(s);
    liveSnaps.push({
      snap: { cap: s.totalCapitalUSD, price: s.currentPrice, pnl },
      basis,
      initialPrice: s.initialPrice > 0 ? s.initialPrice : s.currentPrice,
      useHodlValue: false,
      hodlPnl: Number.isFinite(s.hodlDiffUSD) ? pnl - (s.hodlDiffUSD as number) : 0,
    });
  });
  point(tomorrow, liveSnaps);

  return out;
}

/** "vs HODL" in percentage points (as a fraction): Σ hodlDiffUSD / Σ basis. */
export const vsHodlPp = (bots: IBotData[]): number => {
  // Same scope as every aggregate %: bots with a positive capital basis.
  const ok = bots.filter((b) => capitalBasis(b.stats) > 0);
  const basis = ok.reduce((acc, b) => acc + capitalBasis(b.stats), 0);
  const diff = ok.reduce((acc, b) => acc + (b.stats.hodlDiffUSD || 0), 0);
  return basis > 0 ? diff / basis : 0;
};
